import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { AdminCommunication } from "../shared/communications-types";
import { sortChannels } from "../shared/communication-selectors";
import { ChannelBadge } from "../shared/ChannelBadge";
import { AudienceBadge } from "../shared/AudienceBadge";

interface CommunicationsTableProps {
  readonly communications: readonly AdminCommunication[];
}

export function CommunicationsTable({ communications }: CommunicationsTableProps) {
  const { t } = useTranslation();

  return (
    <ul className="space-y-2">
      {communications.map((comm) => (
        <li key={comm.id} data-testid={`communication-row-${comm.id}`}>
          <Link
            to={`/communications/${comm.id}`}
            data-testid={`communication-link-${comm.id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4 transition-colors hover:border-wander-orange/50"
          >
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{comm.key}</code>
                <AudienceBadge audience={comm.audience} />
                {comm.isCritical && (
                  <span
                    data-testid={`communication-critical-${comm.id}`}
                    className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-300"
                  >
                    {t("communications.flags.critical")}
                  </span>
                )}
              </div>
              {comm.description && (
                <p className="truncate text-sm text-muted-foreground">{comm.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                {sortChannels(comm.channels).map((ch) => (
                  <ChannelBadge key={ch.id} channel={ch.channel} inactive={!ch.isActive} />
                ))}
              </div>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
